'use client';

import React, { useState } from 'react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

/* ─── Mock Data ─────────────────────────────────────────────── */

const revenueData = [
  { month: 'Jan', revenue: 980000, bookings: 4, payouts: 750000 },
  { month: 'Feb', revenue: 1320000, bookings: 6, payouts: 1100000 },
  { month: 'Mar', revenue: 1200000, bookings: 5, payouts: 900000 },
  { month: 'Apr', revenue: 1850000, bookings: 7, payouts: 1600000 },
  { month: 'May', revenue: 2100000, bookings: 9, payouts: 1800000 },
  { month: 'Jun', revenue: 1750000, bookings: 6, payouts: 1500000 },
  { month: 'Jul', revenue: 2400000, bookings: 10, payouts: 2100000 },
  { month: 'Aug', revenue: 3100000, bookings: 13, payouts: 0 },
];

const payoutHistory = [
  { id: 'PO-0081', date: 'Aug 05, 2026', method: 'MTN Mobile Money', amount: 1500000, status: 'completed' },
  { id: 'PO-0074', date: 'Jul 18, 2026', method: 'Airtel Money', amount: 2100000, status: 'completed' },
  { id: 'PO-0068', date: 'Jun 30, 2026', method: 'MTN Mobile Money', amount: 1500000, status: 'completed' },
  { id: 'PO-0061', date: 'Jun 02, 2026', method: 'Bank Transfer', amount: 1800000, status: 'completed' },
  { id: 'PO-0055', date: 'May 14, 2026', method: 'MTN Mobile Money', amount: 900000, status: 'completed' },
];

const pendingPayments = [
  {
    id: 'BK-2041',
    guest: 'Amara Nwosu',
    property: 'Lakeside Villa',
    nights: 5,
    checkIn: 'Aug 22, 2026',
    amount: 900000,
    status: 'pending_release',
    note: 'Releases in 2 days',
  },
  {
    id: 'BK-2038',
    guest: 'David Osei',
    property: 'Bwindi Lodge',
    nights: 3,
    checkIn: 'Aug 19, 2026',
    amount: 750000,
    status: 'processing',
    note: 'Processing payout',
  },
  {
    id: 'BK-2035',
    guest: 'Fatima Al-Hassan',
    property: 'Kampala Apt',
    nights: 4,
    checkIn: 'Aug 14, 2026',
    amount: 380000,
    status: 'pending_release',
    note: 'Releases in 5 days',
  },
  {
    id: 'BK-2029',
    guest: 'James Kariuki',
    property: 'Jinja Cottage',
    nights: 2,
    checkIn: 'Aug 10, 2026',
    amount: 230000,
    status: 'on_hold',
    note: 'Guest review pending',
  },
];

/* ─── Helpers ───────────────────────────────────────────────── */

const fmt = (v: number) =>
  v >= 1_000_000
    ? `UGX ${(v / 1_000_000).toFixed(1)}M`
    : `UGX ${(v / 1_000).toFixed(0)}K`;

const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
  pending_release: { label: 'Pending Release', color: 'text-amber-700', bg: 'bg-amber-50' },
  processing: { label: 'Processing', color: 'text-blue-700', bg: 'bg-blue-50' },
  on_hold: { label: 'On Hold', color: 'text-[#4088C6]', bg: 'bg-[#4088C6]/10' },
  completed: { label: 'Completed', color: 'text-emerald-700', bg: 'bg-emerald-50' },
};

type Period = '3m' | '6m' | 'ytd';

/* ─── Custom Tooltip ────────────────────────────────────────── */

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-lg p-3 text-sm">
      <p className="font-bold text-gray-800 mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.dataKey} style={{ color: p.color }} className="font-semibold">
          {p.name}: {fmt(p.value)}
        </p>
      ))}
    </div>
  );
};

/* ─── Component ─────────────────────────────────────────────── */

export default function EarningsAnalytics() {
  const [period, setPeriod] = useState<Period>('6m');
  const [activeChart, setActiveChart] = useState<'area' | 'bar'>('area');

  const sliced =
    period === '3m' ? revenueData.slice(-3) : period === 'ytd' ? revenueData : revenueData.slice(-6);

  const totalRevenue = sliced.reduce((s, d) => s + d.revenue, 0);
  const totalPayouts = sliced.reduce((s, d) => s + d.payouts, 0);
  const totalPending = pendingPayments.reduce((s, p) => s + p.amount, 0);
  const totalBookings = sliced.reduce((s, d) => s + d.bookings, 0);

  const kpis = [
    {
      label: 'Booking Revenue',
      value: fmt(totalRevenue),
      sub: `${totalBookings} bookings`,
      trend: '+22%',
      trendUp: true,
      icon: '📈',
      accent: 'from-[#4088C6] to-[#3070a6]',
    },
    {
      label: 'Total Payouts',
      value: fmt(totalPayouts),
      sub: 'Transferred out',
      trend: '+18%',
      trendUp: true,
      icon: '💸',
      accent: 'from-violet-500 to-purple-400',
    },
    {
      label: 'Pending Payments',
      value: fmt(totalPending),
      sub: `${pendingPayments.length} transactions`,
      trend: null,
      trendUp: false,
      icon: '⏳',
      accent: 'from-amber-500 to-orange-400',
    },
    {
      label: 'Net Retained',
      value: fmt(totalRevenue - totalPayouts),
      sub: 'Available balance',
      trend: '+31%',
      trendUp: true,
      icon: '🏦',
      accent: 'from-emerald-500 to-teal-400',
    },
  ];

  return (
    <div className="space-y-6" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div
            key={k.label}
            className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 relative overflow-hidden"
          >
            <div className={`absolute top-0 right-0 w-20 h-20 rounded-bl-[60px] bg-gradient-to-br ${k.accent} opacity-10`} />
            <div className="text-2xl mb-2">{k.icon}</div>
            <div className="text-xl font-bold text-gray-900 leading-tight">{k.value}</div>
            <div className="text-sm font-semibold text-gray-600 mt-0.5">{k.label}</div>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-xs text-gray-400">{k.sub}</span>
              {k.trend && (
                <span className={`text-xs font-bold ${k.trendUp ? 'text-emerald-600' : 'text-amber-500'}`}>
                  {k.trendUp ? '↑' : '↓'} {k.trend}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ── Revenue Trend Chart ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div>
            <h3 className="font-bold text-gray-900 text-base">Booking Revenue Trends</h3>
            <p className="text-xs text-gray-400 mt-0.5">Revenue vs payouts over time</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Chart type toggle */}
            <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
              {(['area', 'bar'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setActiveChart(t)}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                    activeChart === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                  }`}
                >
                  {t === 'area' ? '〰 Area' : '▌Bar'}
                </button>
              ))}
            </div>
            {/* Period toggle */}
            <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
              {(['3m', '6m', 'ytd'] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                    period === p ? 'bg-[#4088C6] text-white shadow-sm' : 'text-gray-500'
                  }`}
                >
                  {p.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={240}>
          {activeChart === 'area' ? (
            <AreaChart data={sliced}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="payGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <YAxis
                tickFormatter={(v) => `${(v / 1_000_000).toFixed(1)}M`}
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Area
                type="monotone"
                dataKey="revenue"
                name="Revenue"
                stroke="#f43f5e"
                strokeWidth={2.5}
                fill="url(#revGrad)"
                dot={{ r: 4, fill: '#f43f5e', strokeWidth: 0 }}
                activeDot={{ r: 6 }}
              />
              <Area
                type="monotone"
                dataKey="payouts"
                name="Payouts"
                stroke="#8b5cf6"
                strokeWidth={2}
                fill="url(#payGrad)"
                dot={{ r: 3, fill: '#8b5cf6', strokeWidth: 0 }}
                activeDot={{ r: 5 }}
              />
            </AreaChart>
          ) : (
            <BarChart data={sliced} barSize={22} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <YAxis
                tickFormatter={(v) => `${(v / 1_000_000).toFixed(1)}M`}
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Bar dataKey="revenue" name="Revenue" fill="#f43f5e" radius={[5, 5, 0, 0]} />
              <Bar dataKey="payouts" name="Payouts" fill="#8b5cf6" radius={[5, 5, 0, 0]} />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>

      {/* ── Pending Payments + Payout History ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">

        {/* Pending Payments */}
        <div className="lg:col-span-3 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-gray-900 text-base">Pending Payments</h3>
              <p className="text-xs text-gray-400 mt-0.5">Funds awaiting release or processing</p>
            </div>
            <span className="text-xs font-bold text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full">
              {fmt(totalPending)} held
            </span>
          </div>

          <div className="space-y-3">
            {pendingPayments.map((p) => {
              const s = statusConfig[p.status];
              return (
                <div
                  key={p.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-mono text-gray-400">{p.id}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s.bg} ${s.color}`}>
                        {s.label}
                      </span>
                    </div>
                    <div className="text-sm font-semibold text-gray-800 truncate">
                      {p.guest} · {p.property}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {p.nights} nights · Check-in {p.checkIn} · {p.note}
                    </div>
                  </div>
                  <div className="text-sm font-bold text-gray-900 whitespace-nowrap">
                    {fmt(p.amount)}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
            <span className="text-xs text-gray-400">Payments auto-release 24h after check-out</span>
            <button className="text-xs font-semibold text-[#4088C6] hover:text-[#3070a6] transition-colors">
              Request Early Release →
            </button>
          </div>
        </div>

        {/* Payout History */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="mb-4">
            <h3 className="font-bold text-gray-900 text-base">Payout History</h3>
            <p className="text-xs text-gray-400 mt-0.5">Recent transfers to your account</p>
          </div>

          <div className="space-y-3">
            {payoutHistory.map((po) => {
              const s = statusConfig[po.status];
              return (
                <div key={po.id} className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-xs font-mono text-gray-400">{po.id}</span>
                      <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${s.bg} ${s.color}`}>
                        {s.label}
                      </span>
                    </div>
                    <div className="text-xs font-semibold text-gray-700 truncate">{po.method}</div>
                    <div className="text-xs text-gray-400">{po.date}</div>
                  </div>
                  <div className="text-sm font-bold text-emerald-600 whitespace-nowrap">
                    +{fmt(po.amount)}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-5 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500 font-semibold">Total paid out (YTD)</span>
              <span className="text-sm font-bold text-gray-900">
                {fmt(payoutHistory.reduce((s, p) => s + p.amount, 0))}
              </span>
            </div>
            <button className="w-full mt-2 py-2 rounded-xl border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
              View Full Payout History
            </button>
          </div>
        </div>
      </div>

      {/* ── Revenue by Property (mini breakdown) ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <h3 className="font-bold text-gray-900 text-base mb-4">Revenue by Property</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { name: 'Lakeside Villa', revenue: 1800000, bookings: 8, pct: 58, color: '#f43f5e' },
            { name: 'Kampala Apt', revenue: 570000, bookings: 3, pct: 18, color: '#8b5cf6' },
            { name: 'Bwindi Lodge', revenue: 500000, bookings: 2, pct: 16, color: '#f59e0b' },
            { name: 'Jinja Cottage', revenue: 230000, bookings: 2, pct: 8, color: '#10b981' },
          ].map((prop) => (
            <div key={prop.name} className="p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: prop.color }} />
                <span className="text-xs font-semibold text-gray-700 truncate">{prop.name}</span>
              </div>
              <div className="text-lg font-bold text-gray-900">{fmt(prop.revenue)}</div>
              <div className="text-xs text-gray-400 mt-0.5">{prop.bookings} bookings</div>
              <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${prop.pct}%`, backgroundColor: prop.color }}
                />
              </div>
              <div className="text-xs text-gray-400 mt-1">{prop.pct}% of total</div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
